<script lang="ts">import { stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { isSuccessStatus, Status } from "@infinitro/global";
import { onMount } from "svelte";
import { stateError, stateFatalError } from "../state";
let submitting = false;
export let label = " ";
export let numInputs = 6;
export let enabled = true;
export let handleOtpInput;
let error = false;
let valid = undefined;
let status = undefined;
let invalidCodes = [];
let inputs = [];
let otp = Array(numInputs).fill("");
const handleInput = (index, event) => {
	try {
		if (!enabled) return;
		const input = event.target;
		const value = input.value.replace(/\D/g, "");
		if (value.length > 1) {
			const chars = value.split("").slice(0, numInputs - index);
			chars.forEach((ch, i) => {
				otp[index + i] = ch;
				inputs[index + i].value = ch;
			});
			const last = Math.min(index + chars.length - 1, numInputs - 1);
			if (last < numInputs - 1) inputs[last + 1].focus();
			handleOtpCheck();
			return;
		}
		if (value.length === 1) {
			otp[index] = value;
			if (index < numInputs - 1) {
				inputs[index + 1].focus();
			}
		} else {
			otp[index] = "";
		}
		input.value = value;
		handleOtpCheck();
	} catch (error) {
		console.log("handleInput error:", error);
	}
};
const handleOtpCheck = async () => {
	try {
		const otpCode = otp.join("");
		if (otpCode.length === numInputs) {
			if (otp[0] === "0" || invalidCodes.includes(otpCode)) {
				valid = false;
				console.log(`already known to be invalid: ${otpCode}`);
				return;
			}
			if (handleOtpInput && typeof handleOtpInput === "function") {
				submitting = true;
				let errorMessage = undefined;
				[valid, status, errorMessage] = await handleOtpInput(parseInt(otpCode));
				submitting = false;
				error = status !== undefined && !isSuccessStatus(status);
				stateFatalError.set(status === Status.TOO_MANY_ATTEMPTS);
				if (valid === false) invalidCodes.push(otpCode);
			} else {
				console.log("handleOtpInputComplete is not a function");
			}
		} else {
			valid = undefined;
			error = false;
			status = undefined;
			stateError.set(undefined);
		}
	} catch (error) {
		submitting = false;
		console.log("handleOtpInputComplete error:", error);
	}
};
const handlePaste = (event) => {
	try {
		if (!enabled) return;
		event.preventDefault();
		const paste = event.clipboardData?.getData("text") ?? "";
		const numericPaste = paste.replace(/\D/g, "");
		if (numericPaste.length > 0) {
			const pasteArray = numericPaste.split("").slice(0, numInputs);
			for (let i = 0; i < pasteArray.length && i < numInputs; i++) {
				otp[i] = pasteArray[i];
				inputs[i].value = pasteArray[i];
				if (inputs[i + 1]) {
					inputs[i + 1].focus();
				}
			}
			handleOtpCheck();
		}
	} catch (error) {
		console.log("hadlePast error:", error);
	}
};
const handleKeyUp = (_index, event) => {
	if (!enabled) return;
	if (event.key === "Backspace") {
		handleOtpCheck();
	}
};
const handleKeyDown = (index, event) => {
	if (!enabled) return;
	if (event.key === "Backspace") {
		if (otp[index] === "" && index > 0) inputs[index - 1].focus();
	}
	// Check if Ctrl or Cmd is pressed along with 'V' for paste operations
	if (event.key === "v" && (event.ctrlKey || event.metaKey)) {} else if (!/^[0-9]$/.test(event.key) && event.key !== "Backspace") {
		event.preventDefault();
	} else {
		// Clear this and all subsequent inputs for input
		inputs.forEach((_, i) => {
			if (i >= index) {
				otp[i] = "";
				inputs[i].value = "";
			}
		});
	}
};
const reset = () => {
	valid = undefined;
	error = false;
	status = undefined;
	inputs.forEach((_, i) => {
		otp[i] = "";
		inputs[i].value = "";
	});
	inputs[0].focus();
};
onMount(() => {
	reset();
});
$: useAlternative = $stateUi.input.inputStyle === "alternative";
$: useAlternative2 = $stateUi.emailVerify.emailVerifyStyle === "alternative2";
</script>

<p class="message {valid === false || error === true ? 'error' : ''}">
	{label}
</p>

<div class="otp-input-container">
	{#each Array(numInputs) as _, index}
		<div
			class="otp-input-wrapper {valid === false ? 'error' : ''} {valid === true ? 'valid' : ''}"
			class:otp-input-alternative2={useAlternative2}
			class:otp-input-alternative={useAlternative}
		>
			<input
				id={`verificationCodeInput${index + 1}`}
				class="otp-input {valid === false ? 'error' : ''} {valid === true ? 'valid' : ''}"
				class:otp-input-alternative={useAlternative}
				type="number"
				placeholder="-"
				bind:this={inputs[index]}
				maxlength="1"
				on:input={(event) => handleInput(index, event)}
				on:keydown={(event) => handleKeyDown(index, event)}
				on:keyup={(event) => handleKeyUp(index, event)}
				on:paste={handlePaste}
				disabled={submitting || valid || $stateFatalError || !enabled}
			/>
		</div>
	{/each}
</div>

<style>
	.otp-input-container {
		display: flex;
		justify-content: space-between;
		padding-bottom: 0px;
}
	.otp-input-wrapper {
		position: relative;
		width: 16%;
		aspect-ratio: 1/1;
}
	.otp-input {
		height: 100%;
		min-height: 48px;
		width: 100%;
		border-radius: 0.75rem;
		border-width: 2px;
		border-color: var(--color-background-100);
		background-color: transparent;
		text-align: center;
		font-size: 1.5rem;
		line-height: 2rem;
		outline: 2px solid transparent;
		outline-offset: 2px;
}
	.otp-input-wrapper.otp-input-alternative {
		/* no extra wrapper styles needed */
	}
	.otp-input.otp-input-alternative {
		border-color: var(--color-accent-700);
}
	.otp-input-wrapper.otp-input-alternative2 {
		border-radius: 8px;
		background: linear-gradient(
			180deg,
			rgba(var(--color-background-900-rgb) / 0.6) 0%,
			rgba(var(--color-background-900-rgb) / 0.5) 100%
		);
}
	.otp-input-wrapper.otp-input-alternative2 .otp-input {
		border-radius: 8px;
		border-style: none;
		background-color: transparent;
}

	.otp-input-wrapper.otp-input-alternative2::before {
		position: absolute;
		inset: 0px;
		border-radius: 8px;
		padding: 1px;
		content: '';
		background: linear-gradient(180deg, rgba(255, 255, 255, 0.3) 0%, transparent 100%);
		-webkit-mask:
			linear-gradient(#fff 0 0) content-box,
			linear-gradient(#fff 0 0);
		-webkit-mask-composite: xor;
		mask-composite: exclude;
		pointer-events: none;
		z-index: 2;
}
	.otp-input:focus {
		border-color: var(--color-primary-500);
}
	.otp-input.error {
		border-color: var(--color-error-500);
}
	.otp-input.valid {
		border-color: var(--color-primary-500);
}
	.otp-input-wrapper.otp-input-alternative2:focus-within::before {
		background: linear-gradient(
			180deg,
			rgba(var(--color-primary-500-rgb) / 0.8) 0%,
			transparent 100%
		);
	}
	.message {
		margin-bottom: 0.75rem;
		text-align: center;
		font-size: 0.75rem;
		line-height: 1rem;
		color: var(--color-text-300);
}
	.error {
		color: var(--color-error-500);
}
	.otp-input::-moz-placeholder {
		color: var(--color-text-300);
		opacity: 1;
}
	.otp-input::placeholder {
		color: var(--color-text-300);
		opacity: 1;
}
	.otp-input:focus::-moz-placeholder {
		opacity: 0;
}
	.otp-input:focus::placeholder {
		opacity: 0;
}
	/* Chrome, Safari, Edge, Opera */
	input[type='number']::-webkit-outer-spin-button,
	input[type='number']::-webkit-inner-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}
	/* Firefox */
	input[type='number'] {
		-moz-appearance: textfield;
	}

	.otp-input-wrapper.otp-input-alternative2.error::before {
		background: none;
	}

	.otp-input-wrapper.otp-input-alternative2.error::after {
		content: '';
		position: absolute;
		inset: 0;
		border-radius: 8px;
		border: 1px solid transparent;
		pointer-events: none;
		z-index: 3;
		border-color: var(--color-error-500);
	}
</style>
